import React from 'react';
import './Legal.css';

export default function Privacy() {
  return (
    <div className="legal-page">
      <div className="container">
        <div className="legal-card card">
          <h1>Privacy Policy</h1>
          <p className="legal-updated">Last updated: September 29, 2026</p>

          <section>
            <h2>What We Collect</h2>
            <p>
              Petlings collects the minimum data needed to provide the service:
            </p>
            <ul>
              <li><strong>Account identifiers</strong> - an email address if you sign up by email, or an Apple identifier if you use Sign in with Apple, and your Minecraft UUID and username once you link a Minecraft account (verified through Mojang's session service). Used for login, password resets and verification codes. We do not send marketing email.</li>
              <li><strong>Pet data</strong> - your adopted pets, their names, stats, cosmetics, gardens and positions. Synced between the mod, the website and the iOS app.</li>
              <li><strong>Chat messages</strong> - conversations with your pet are stored so the pet remembers them and so your history is the same on every device. To write a reply, the message and recent history are sent to an AI model: our own hosted models where possible, or Groq as a fallback. Younger players' personal details are never kept or sent (see Ages).</li>
              <li><strong>Voice</strong> - the microphone is push-to-talk. What you say is turned into text on your phone where the device supports it, and only the text is sent to the pet. We do not store recordings.</li>
              <li><strong>Game journal</strong> - with the Minecraft mod installed and your account linked, the mod reports small moments from play: a session starting and ending, a change of dimension, a notable biome, a death and what caused it, a close call, a level milestone, a first find of a notable item, an advancement, and interactions with your pet. No chat from the game, no coordinates and no other players' names are sent. The journal is what your pet remembers and talks about.</li>
              <li><strong>Economy data</strong> - your treat balance, purchases and transaction history. Purchases in the iOS app go through the App Store; we receive the transaction identifier, never your payment details.</li>
              <li><strong>Photos</strong> - photos you take with your pet stay on your phone. The app only adds to your photo library; it never reads it.</li>
            </ul>
          </section>

          <section>
            <h2>What We Don't Collect</h2>
            <ul>
              <li>We do not store Microsoft, Xbox, or Mojang access tokens</li>
              <li>We do not track your location or browsing activity, and the game journal is limited to the moments listed above</li>
              <li>We do not collect data from other mods you have installed</li>
              <li>We do not sell or share your data with third parties for advertising</li>
            </ul>
          </section>

          <section>
            <h2>Third-Party Services</h2>
            <ul>
              <li><strong>Groq</strong> - processes AI chat messages when our own models are unavailable. See <a href="https://groq.com/privacy" target="_blank" rel="noopener noreferrer">Groq Privacy Policy</a></li>
              <li><strong>Apple</strong> - Sign in with Apple, App Store purchases, and on-device speech recognition on iPhone. See <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer">Apple Privacy Policy</a></li>
              <li><strong>Resend</strong> - sends verification emails. See <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">Resend Privacy Policy</a></li>
              <li><strong>Tebex</strong> - processes treat purchases. See <a href="https://www.tebex.io/legal/privacy" target="_blank" rel="noopener noreferrer">Tebex Privacy Policy</a></li>
              <li><strong>DigitalOcean</strong> - hosts our servers and database</li>
            </ul>
          </section>

          <section>
            <h2>Data Storage & Security</h2>
            <p>
              Your data is stored in a PostgreSQL database hosted on DigitalOcean. We use HTTPS for all communications,
              bcrypt for password hashing, and JWT tokens for authentication. Email verification codes expire after 10 minutes.
            </p>
          </section>

          <section>
            <h2>Your Rights</h2>
            <p>
              You can delete your account and everything held for it yourself, in the iOS app (Profile &rsaquo; Settings &rsaquo; Delete account)
              or by contacting us at{' '}<a href="mailto:privacy@petlings.gg">privacy@petlings.gg</a>.
              Deletion is immediate in the app; emailed requests are processed within 30 days. You can also unlink a Minecraft
              account at any time and keep the rest of your account.
            </p>
          </section>

          <section>
            <h2>Ages</h2>
            <p>
              Petlings is for players aged 10 and older. The iOS app asks for an age range at the start and adjusts:
              for players under 13 the pet does not ask for, keep or send personal details, memories of the player are not
              created, and no personal information is collected beyond what the account needs to work. Where the age is
              unknown, the app treats the player as a child. If we learn we have collected personal information from a child
              that we should not have, we delete it.
            </p>
          </section>

          <section>
            <h2>Changes</h2>
            <p>
              We may update this policy from time to time. Changes will be posted on this page with an updated date.
            </p>
          </section>

          <section>
            <h2>Contact</h2>
            <p>
              Questions about this policy? Email us at <a href="mailto:privacy@petlings.gg">privacy@petlings.gg</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
